import { Alert, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, TextField, MenuItem, Select, InputLabel, SelectChangeEvent, ListSubheader } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import useNswagClient from './../../../../hooks/api/useNswagClient';
import { InventoryExtensionReportRequest, Store } from './../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { UserContext } from './../../../../components/shared/useUser';
import dayjs from 'dayjs';
import { ProductType } from '../../../../enums/ProductType';
import { filterStoreOptions } from '../../../../utils';

// Group months by year for dropdown
const monthOptions = Array.from({ length: 13 }).map((_, idx) => {
  const date = dayjs().subtract(idx, 'month');
  return {
    label: date.format('MMMM'),
    value: date.month() + 1,
    year: date.year(),
  };
});

const groupedMonthOptions = monthOptions.reduce((acc, curr) => {
  acc[curr.year] = acc[curr.year] || [];
  acc[curr.year].push(curr);
  return acc;
}, {} as Record<number, { label: string; value: number; year: number }[]>);

type IProps = {
    onClose?: () => void;
}

const InventoryExtensionReportForm = (props: IProps) => {
  const { user } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { runInventoryExtensionReport } = useNswagClient();
  const { t } = useTranslation('common');

  // Prepare product type options from enum
  const productTypeOptions = Object.entries(ProductType)
    .filter(([key, value]) => !isNaN(Number(value)))
    .map(([key, value]) => ({ label: key, value: Number(value) }));

  // Select all product types by default
  const [selectedProductTypes, setSelectedProductTypes] = useState<{ label: string, value: number }[]>(
    productTypeOptions,
  );
  const [checkedAllProductTypes, setCheckedAllProductTypes] = useState<boolean>(true);
  const [selectedMonth, setSelectedMonth] = useState<number | ''>('');
  const [selectedYear, setSelectedYear] = useState<number | ''>('');

  useEffect(() => {
    setCheckedAllProductTypes(selectedProductTypes.length === productTypeOptions.length);
  }, [selectedProductTypes, productTypeOptions.length]);

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setSelectedStores([]);

    setCheckedAllStores(checked);
  };

  const handleSelectAllProductTypesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setCheckedAllProductTypes(checked);
    setSelectedProductTypes(checked ? productTypeOptions : []);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const handleProductTypeChange = (event: SyntheticEvent, value: { label: string, value: number }[]) => {
    setSelectedProductTypes(value);
  };

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    const [monthStr, yearStr] = event.target.value.split('-');
    setSelectedMonth(Number(monthStr));
    setSelectedYear(Number(yearStr));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsGenerating(true);
    setErrorMessage('');
    setSuccessMessage('');

    const selectedAllStores = checkedAllStores || (selectedStores.length > 0 && selectedStores.length == user?.stores?.length);

    const year = Number(selectedYear) || dayjs().year();
    const month = Number(selectedMonth);

    const request: InventoryExtensionReportRequest = {
      reportOnAllAvailableStores: selectedAllStores,
      stores: selectedAllStores ? undefined : selectedStores.map(x => x.storeNumber as string),
      productTypeIds: selectedProductTypes.length === productTypeOptions.length
        ? []
        : selectedProductTypes.map(pt => pt.value),
      month: month,
      year: year,
    };

    runInventoryExtensionReport(request).then(() => {
      setSuccessMessage(t('reporting.generic.post-generation-message'));
      setIsRequestSubmitted(true);
    }).catch((error) => {
      if (typeof (error) === 'string') {
        setErrorMessage(error);
      }
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              multiple
              limitTags={2}
              options={user?.stores ?? []}
              value={selectedStores}
              onChange={handleSelectChange}
              disabled={checkedAllStores}
              isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
              getOptionLabel={(option) => option.storeName ?? ''}
              filterOptions={filterStoreOptions}
              renderInput={(params) => (
                <TextField {...params}
                  required={selectedStores.length === 0 && !checkedAllStores}
                  label={t('reporting.select-stores')}
                  placeholder={t('reporting.select-stores')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={<Checkbox
                checked={checkedAllStores}
                onChange={handleSelectAllChange} />}
              label={t('reporting.all-stores')}
            />
          </FormGroup>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={productTypeOptions}
              getOptionLabel={option => option.label}
              value={selectedProductTypes}
              onChange={handleProductTypeChange}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params}
                  required={selectedProductTypes.length === 0}
                  label={t('reporting.product-types')}
                  placeholder={t('reporting.product-types')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedAllProductTypes}
                  onChange={handleSelectAllProductTypesChange}
                />
              }
              label={t('reporting.product-types-all')}
            />
          </FormGroup>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth
            required
            size="small">
            <InputLabel id="month-select-label">{t('reporting.selectMonth')}</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              value={selectedMonth && selectedYear ? `${selectedMonth}-${selectedYear}` : ''}
              label={t('reporting.selectMonth')}
              onChange={handleMonthChange}
              required
              MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
              renderValue={selected => {
                if (!selected) return '';
                const [monthStr, yearStr] = String(selected).split('-');
                const found = monthOptions.find(
                  m => m.value === Number(monthStr) && m.year === Number(yearStr),
                );
                return found ? `${found.label} ${found.year}` : '';
              }}
            >
              {Object.keys(groupedMonthOptions)
                .sort((a, b) => Number(b) - Number(a))
                .flatMap(year => [
                  <ListSubheader key={year}>{year}</ListSubheader>,
                  ...groupedMonthOptions[Number(year)].map(month => (
                    <MenuItem key={`${month.value}-${month.year}`}
                      value={`${month.value}-${month.year}`}>
                      {month.label}
                    </MenuItem>
                  )),
                ])}
            </Select>
          </FormControl>
        </Grid>
        {
          errorMessage &&
                    <Grid item
                      xs={12}>
                      <Alert severity="error">{errorMessage}</Alert>
                    </Grid>
        }
        {
          successMessage &&
                    <Grid item
                      xs={12}>
                      <Alert severity="success">{successMessage}</Alert>
                    </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary'
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default InventoryExtensionReportForm;